import { useEffect, useState } from 'react';
import axios from 'axios';
import { logError } from '../utils/errorHandler';

export interface AgeSpan {
  raceName: string;
  stage: string;
  thresholdDays: number;
}

export type AgeSpanMap = Record<string, Array<Pick<AgeSpan, 'stage' | 'thresholdDays'>>>;

let cachedAgeSpans: AgeSpanMap | null = null;
let inflight: Promise<AgeSpanMap> | null = null;

const buildAgeSpanMap = (spans: AgeSpan[]): AgeSpanMap => {
  return spans.reduce<AgeSpanMap>((acc, span) => {
    if (!acc[span.raceName]) {
      acc[span.raceName] = [];
    }
    acc[span.raceName].push({ stage: span.stage, thresholdDays: span.thresholdDays });
    acc[span.raceName].sort((a, b) => a.thresholdDays - b.thresholdDays);
    return acc;
  }, {});
};

const fetchAgeSpans = async (): Promise<AgeSpanMap> => {
  if (cachedAgeSpans) return cachedAgeSpans;
  if (inflight) return inflight;

  inflight = axios
    .get('/api/mercenary/age-spans')
    .then((res) => res.data as Record<string, Array<{ stage: string; thresholdDays: number }>>)
    .then((data) => buildAgeSpanMap(
      Object.entries(data || {}).flatMap(([raceName, spans]) =>
        (spans || []).map((s) => ({ raceName, stage: s.stage, thresholdDays: s.thresholdDays }))
      )
    ))
    .then((map) => {
      cachedAgeSpans = map;
      return map;
    })
    .catch((err) => {
      logError('Failed to fetch age spans', err);
      throw err;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

export const getAgeStageLabel = (race: string, age: number, map: AgeSpanMap): string | null => {
  const stages = map[race];
  if (!stages || stages.length === 0) return null;

  let currentStage: string | null = 'Young';
  for (const stage of stages) {
    if (age >= stage.thresholdDays) {
      currentStage = stage.stage;
    } else {
      break;
    }
  }
  return currentStage;
};

export const useAgeStageLabel = (race?: string, age?: number) => {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!race || age == null) return undefined;
    let mounted = true;

    fetchAgeSpans()
      .then((map) => {
        if (!mounted) return;
        setLabel(getAgeStageLabel(race, age, map));
      })
      .catch(() => {
        if (!mounted) return;
        setLabel(null);
      });

    return () => {
      mounted = false;
    };
  }, [race, age]);

  return label;
};
